import { createRoute, Link, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { useGetInvestigationById } from '@/application/services/investigation.service'
import { Breadcrumb } from '@/ui/components/common/Breadcrumb'
import { Button } from '@/ui/components/common/Button'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { MarkdownView } from '@/ui/components/common/MarkdownView'
import { PreviewArticleButton } from '@/ui/pages/management/investigations/PreviewArticleButton'
import { investigationsRoute } from '@/ui/pages/management/investigations'
import { InvestigationNotesSection } from './components/InvestigationNotesSection'
import { LeadsSection } from './components/LeadsSection'
import { TeamManagementSection } from './components/TeamManagementSection'

export const investigationsLeadsRoute = createRoute({
  getParentRoute: () => investigationsRoute,
  path: '$id/leads',
  component: InvestigationLeadsPage,
})

export function InvestigationLeadsPage() {
  const { t } = useTranslation()
  const { id } = useParams({ strict: false })

  const { data: investigation, isLoading: invLoading } = useGetInvestigationById(id ?? '')

  if (invLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/manage/investigations" search={{ statusFilter: 'active' }}>
            <Button variant="outline" size="sm" leftIcon={<ArrowLeft className="w-4 h-4" />}>
              {t('admin.inv_leads_back')}
            </Button>
          </Link>
        </div>
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!investigation) {
    return (
      <div className="space-y-6">
        <Link to="/manage/investigations" search={{ statusFilter: 'active' }}>
          <Button variant="outline" size="sm" leftIcon={<ArrowLeft className="w-4 h-4" />}>
            {t('admin.inv_leads_back')}
          </Button>
        </Link>
        <p className="text-muted-foreground">{t('admin.leads_by_inv_not_found')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[
          { label: t('admin.nav_investigations'), href: '/manage/investigations' },
          { label: investigation.name },
        ]}
      />

      {/* ─── Investigation header ─── */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1 min-w-0">
          <h2 className="text-2xl font-bold">{investigation.name}</h2>
          <MarkdownView
            content={investigation.description}
            className="text-sm text-muted-foreground"
          />
        </div>
        <PreviewArticleButton investigation={investigation} />
      </div>

      <TeamManagementSection investigationId={id!} authorId={investigation.authorId} />
      <InvestigationNotesSection id={id!} />
      <LeadsSection investigationId={id!} />
    </div>
  )
}
